// # 실패를 사람의 말로 — 화면 언어로 옮기는 곳은 여기 하나다
//
// 클라이언트(`@maru/client`)는 화면 언어를 모른다. 그래서 오류의 `message` 를 그대로
// 띄우면 영어 화면에도 한국어가 뜬다. 여기서는 문장이 아니라 **모양**으로 가른다:
//
// - `ApiError` — `status`·`code`·`said`(서버가 몸통에 적은 말 그대로). 409 의 `said` 는
//   기계용 이유(`ai_off`)라 `REASONS` 로 옮긴다. 서버의 한국어 문장은 **그대로 띄우지 않는다** —
//   로그용 평서문("길이 초과: name (최대 80, 실제 120)", "사설·루프백 주소로는 배달하지 않는다")이라
//   사람에게는 내부 사정이다. 아는 모양만 `SHAPES` 로 옮기고, 모르면 상태로 만든 문장을 쓴다
//   (docs/design3.md F10).
// - `UploadTooBig`·`UploadRejected`·`PictureUnreadable` — `name` 으로 가른다.
// - `TypeError` — `fetch` 가 서버에 닿지 못했다.
//
// ui 는 `@maru/client` 를 임포트하지 않는다(AGENTS.md §2-1). 모양으로만 알아본다.

import { ATTACHMENT_MAX_BYTES } from '@maru/protocol'
import { type Locale, locale_now, msg, translate } from './i18n.ts'

/**
 * 서버의 기계용 이유(러스트 `PortError::Conflict("…")`) → 원인과 다음 행동.
 * 이 표는 첫 화면에 실린다. 설정 판에서만 나는 이유는 그 판이 `overrides` 로 준다
 * (`sso_secret_needed` → SSO 판, `url_verification_failed` → 연동 설정, 캔버스 → E23 에서 캔버스 판). SCIM 이유는 IdP 가
 * 받으므로 화면에 오지 않는다.
 */
export const REASONS: Readonly<Record<string, string>> = {
  ai_off: msg('AI 기능이 꺼져 있어요. 워크스페이스 관리자에게 문의해 주세요.'),
  ai_busy: msg('AI가 지금 붐벼요. 잠시 후 다시 시도해 주세요.'),
  ai_refused: msg('AI가 이 내용에는 답하지 않았어요.'),
  huddle_off: msg('이 서버에서는 허들이 꺼져 있어요.'),
  huddle_full: msg('허들이 가득 찼어요(최대 50명).'),
  sso_required: msg('SSO로 로그인하는 워크스페이스예요. 아래 “SSO로 로그인”을 눌러 주세요.'),
  sso_rejected: msg('IdP가 로그인을 거절했어요. IdP 관리자에게 문의해 주세요.'),
  sso_unreachable: msg('IdP에 연결하지 못했어요. 잠시 후 다시 시도해 주세요.'),
  sso_no_email: msg('IdP가 이메일 주소를 보내지 않았어요. IdP 설정을 확인해 주세요.'),
  app_unreachable: msg('앱이 응답하지 않았어요. 잠시 후 다시 눌러 주세요.'),
  no_interactivity: msg('이 앱은 버튼 입력을 받지 않아요.'),
  bad_app_reply: msg('앱이 알 수 없는 응답을 보냈어요.'),
  expired_trigger_id: msg('시간이 지나 창을 열 수 없어요. 다시 눌러 주세요.'),
  push_limit_reached: msg('창을 더 겹쳐 열 수 없어요(최대 3개).'),
  hash_conflict: msg('그사이 창이 바뀌었어요. 다시 시도해 주세요.'),
  stale_view: msg('그사이 창이 바뀌었어요. 다시 시도해 주세요.'),
  not_in_channel: msg('채널 구성원만 할 수 있어요.'),
  user_not_in_channel: msg('받는 사람이 이 채널의 구성원이 아니에요.'),
  slug_taken: msg('이미 쓰이는 주소예요. 주소를 바꾸고 코드를 다시 받아 주세요.'),
}

/**
 * 서버 문장의 모양 → 사람의 말. 도메인 오류(`DomainError` 의 `Display`)와, 사람이 고칠 수 있는
 * 충돌 문장만 둔다. 모양이 바뀌면 상태 문장으로 떨어질 뿐 틀린 말을 하지는 않는다.
 */
const SHAPES: readonly (readonly [RegExp, string])[] = [
  // `/remind` 의 까닭은 일반 모양보다 먼저 — "쓸 수 없는 이름이에요" 는 때를 못 읽은 사람에게 틀린 말이다(3차 F148).
  [
    /^허용되지 않는 이름: 때를 못 읽었다/,
    msg('때를 읽지 못했어요. “/remind 10분 뒤 물 마시기”처럼 적어 주세요.'),
  ],
  [/^범위 밖: 리마인더는 최소/, msg('리마인더는 1분 뒤부터 걸 수 있어요.')],
  [/^범위 밖: 리마인더는 \d+일/, msg('리마인더는 120일 안으로만 걸 수 있어요.')],
  [/^빈 값: 리마인더 내용/, msg('무엇을 알려 드릴지 때 뒤에 적어 주세요.')],
  [/^길이 초과: .*\(최대 (\d+)/, msg('너무 길어요. {n}자까지 쓸 수 있어요.')],
  [/^빈 값:/, msg('비어 있는 칸이 있어요. 채운 뒤 다시 시도해 주세요.')],
  [/ 자리가 찼다 \(최대 (\d+)\)/, msg('더 넣을 수 없어요. 최대 {n}개까지예요.')],
  [/^허용되지 않는 이름/, msg('쓸 수 없는 이름이에요. 다른 이름을 적어 주세요.')],
  [/^범위 밖/, msg('허용 범위를 벗어난 값이에요. 값을 확인해 주세요.')],
  [/^같은 이름의 .+ 있다$/, msg('같은 이름이 이미 있어요. 다른 이름을 적어 주세요.')],
  [/^고칠 것이 없다$/, msg('바뀐 내용이 없어요.')],
  [/^공지 채널은 관리자만/, msg('공지 채널에는 관리자만 글을 쓸 수 있어요.')],
  [
    /^마지막 소유자는/,
    msg('마지막 소유자는 역할을 바꿀 수 없어요. 먼저 다른 사람을 소유자로 정해 주세요.'),
  ],
  [/^먼저 내 2단계 인증을 켜라$/, msg('내 2단계 인증을 먼저 켜 주세요.')],
  [/^2단계 인증이 이미 켜져/, msg('2단계 인증이 이미 켜져 있어요.')],
  [/^2단계 인증이 꺼져/, msg('2단계 인증이 꺼져 있어요.')],
  [/^그 앱은 이미 설치/, msg('이미 설치된 앱이에요.')],
  [/봇을 들일 수 없다$/, msg('이 채널에는 봇을 추가할 수 없어요.')],
  [/^사설·루프백 주소/, msg('사설망 주소로는 보낼 수 없어요. 공개 주소를 적어 주세요.')],
  [/^http\(s\) 만/, msg('http:// 나 https:// 로 시작하는 주소만 쓸 수 있어요.')],
  // 발신 웹훅·커맨드·앱 주소의 거절(3차 F151). 앞에 "상호작용 주소는" 같은 머리가 붙기도 한다.
  [
    /http:\/\/ 또는 https:\/\/ 로 시작해야/,
    msg('http:// 나 https:// 로 시작하는 주소만 쓸 수 있어요.'),
  ],
  [/^받는 쪽이 2xx/, msg('받는 서버가 요청을 거절했어요. 주소와 서버 상태를 확인해 주세요.')],
  [
    /^모르는 스코프/,
    msg(
      '없는 권한 이름이 있어요. messages:read, messages:write, channels:read 중에서 적어 주세요.',
    ),
  ],
  [/범위를 넘는 스코프/, msg('앱에 등록된 범위를 넘는 권한은 요청할 수 없어요.')],
]

/** 서버 문장을 아는 모양이면 옮긴다. */
function shaped(said: string, t: (ko: string, vars?: Record<string, string | number>) => string) {
  for (const [re, ko] of SHAPES) {
    const m = re.exec(said)
    if (m !== null) return t(ko, m[1] === undefined ? undefined : { n: m[1] })
  }
  return null
}

const TRY_AGAIN = msg('하지 못했어요. 잠시 후 다시 시도해 주세요.')

interface Wire {
  status: number
  code: string | null
  said: string | null
  retry_secs: number | null
}

/** `ApiError` 의 모양. 클래스를 임포트할 수 없으니 이름과 칸으로 알아본다. */
function wire(e: unknown): Wire | null {
  if (!(e instanceof Error) || e.name !== 'ApiError') return null
  const w = e as unknown as Partial<Wire>
  if (typeof w.status !== 'number') return null
  return {
    status: w.status,
    code: typeof w.code === 'string' ? w.code : null,
    said: typeof w.said === 'string' ? w.said : null,
    retry_secs: typeof w.retry_secs === 'number' ? w.retry_secs : null,
  }
}

/** 상태로 만든 문장. 서버가 문장을 안 줬거나 모르는 모양일 때. */
function by_status(w: Wire, locale: Locale): string {
  const t = (ko: string, vars?: Record<string, string | number>) => translate(locale, ko, vars)
  if (w.status === 429)
    return t('요청이 너무 많아요. {n}초 후 다시 시도해 주세요.', { n: w.retry_secs ?? 60 })
  if (w.status === 401) return t('로그인이 만료됐어요. 다시 로그인해 주세요.')
  if (w.status === 404) return t('찾을 수 없어요. 삭제됐거나 볼 수 없는 항목이에요.')
  if (w.code === 'MailUndeliverable')
    return t(
      '메일을 보내지 못했어요. 주소를 확인하거나, 초대라면 초대 링크를 복사해 공유해 주세요.',
    )
  if (w.code === 'TooBroad') return t('검색 범위가 너무 넓어요. 채널이나 검색어로 좁혀 주세요.')
  if (w.code === 'AlreadyExists') return t('이미 있어요.')
  if (w.status === 409) return t('지금은 할 수 없어요. 새로 고친 뒤 다시 시도해 주세요.')
  if (w.status === 400 || w.status === 422) return t('입력한 내용을 확인한 뒤 다시 시도해 주세요.')
  if (w.status >= 500) return t('서버가 잠시 응답하지 않아요. 잠시 후 다시 시도해 주세요.')
  return t('하지 못했어요({status}). 잠시 후 다시 시도해 주세요.', { status: w.status })
}

/**
 * 실패의 까닭 한 줄. 알 수 없으면 다시 해 보라는 말이라도 한다.
 * `overrides` 는 자리마다 더 알맞은 말이 있을 때 이유 표를 덮는다(예: SSO 설정 판).
 */
export function trouble(
  e: unknown,
  locale: Locale = locale_now(),
  overrides: Readonly<Record<string, string>> = {},
): string {
  const t = (ko: string, vars?: Record<string, string | number>) => translate(locale, ko, vars)
  const reason = (key: string | null): string | undefined =>
    key === null ? undefined : (overrides[key] ?? REASONS[key])

  if (e instanceof Error && e.name === 'UploadTooBig') {
    const bytes = (e as Error & { bytes?: unknown }).bytes
    if (bytes === 0) return t('빈 파일은 올릴 수 없어요.')
    return t('파일이 너무 커요. {mib}MB까지 올릴 수 있어요.', {
      mib: Math.floor(ATTACHMENT_MAX_BYTES / 1024 / 1024),
    })
  }
  // 415 는 우리가 지은 값이다 — 사진(얼굴·이모지)의 종류가 목록 밖이라 묻지도 않았다.
  if (e instanceof Error && e.name === 'UploadRejected') {
    if ((e as Error & { status?: unknown }).status === 415)
      return t('PNG·JPEG·WebP·GIF 그림만 쓸 수 있어요.')
    return t('파일을 올리지 못했어요. 잠시 후 다시 시도해 주세요.')
  }
  // 앱이 사진을 줄이려다 읽지 못했다(`app/face.ts`).
  if (e instanceof Error && e.name === 'PictureUnreadable')
    return t('그림을 읽지 못했어요. 다른 파일을 골라 주세요.')

  const w = wire(e)
  if (w !== null) {
    const known = reason(w.said)
    if (known !== undefined) return t(known)
    // 코드로 아는 실패(`TooBroad`·`AlreadyExists`)는 우리 문장이 다음 행동까지 말한다.
    const coded = w.code === 'TooBroad' || w.code === 'AlreadyExists'
    const told = w.said === null || coded || w.status >= 500 ? null : shaped(w.said, t)
    if (told !== null) return told
    return by_status(w, locale)
  }

  // `fetch` 는 서버에 닿지 못하면 `TypeError` 를 던진다. 그 글자는 브라우저마다 다른 영어다.
  if (e instanceof TypeError)
    return t('서버에 연결하지 못했어요. 네트워크를 확인한 뒤 다시 시도해 주세요.')
  if (e instanceof Error) {
    const known = reason(e.message)
    if (known !== undefined) return t(known)
    // 화면이 직접 던진 문장은 이미 사람의 말이다.
    if (e.message !== '') return e.message
  }
  return t(TRY_AGAIN)
}

/**
 * 사람이 그 자리에서 풀 수 있는 실패인가. `'retry'` 면 부르는 쪽이 [다시 시도] 단추를 붙인다 —
 * 서버가 잠깐 무너졌거나(5xx·429·연결 끊김), 공급자가 붐비거나, 올리기가 거절된 경우다.
 * 입력을 고쳐야 하는 실패(400·409)에는 단추를 주지 않는다 — 같은 것을 다시 보내면 같은 답이다.
 */
export function remedy(e: unknown): 'retry' | null {
  if (e instanceof TypeError) return 'retry'
  if (e instanceof Error && e.name === 'UploadRejected') return 'retry'
  const w = wire(e)
  if (w === null) return null
  if (w.status >= 500 || w.status === 429) return 'retry'
  if (w.said === 'ai_busy' || w.said === 'app_unreachable' || w.said === 'sso_unreachable')
    return 'retry'
  return null
}
